import { AlertTriangle } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  MANDATORY_UNRESOLVED_STATES,
  countMandatoryRisk,
  complianceMatrixLink,
  reviewCountPhrase,
  submissionRelevant,
} from '@/lib/compliance-position'
import type { ExtractedComplianceItem } from '@/types/extraction'

interface CompliancePositionBannerProps {
  bidId: string
  items: ExtractedComplianceItem[]
}

/**
 * Pre-decision compliance signal on the Go/No-Go tab (spec §6). Three states.
 *
 * NOT ASSESSED — requirements exist and none carry a verdict. This is the state
 * every bid extracted before the assessment engine shipped sits in, and the
 * honest reading is "we do not know", not "we are fine". Counting only at-risk
 * rows would leave that bid silent, which is the same screen a fully-clear bid
 * shows — and the user is one click from committing the team. So it says so,
 * and points at where to fix it.
 *
 * AT RISK — the counts, separately clickable: a gap to close and a verification
 * to chase are different problems with different next actions.
 *
 * CLEAR (or no matrix at all) — silent. A banner that fires on every bid trains
 * people to ignore it.
 */
export function CompliancePositionBanner({ bidId, items }: CompliancePositionBannerProps) {
  const relevant = submissionRelevant(items)
  // "Has a position" is NOT "the AI has run": a manual position carries no
  // assessed-at stamp, and a bid whose positions were all set by hand must not
  // read as "none assessed" — least of all when one of them is non-comply.
  const hasAnyPosition = relevant.some(
    (item) => item.positionAssessedAt != null || (item.position ?? 'unassessed') !== 'unassessed'
  )
  const { nonComply, needsReview, needsConfirmation, atRisk } = countMandatoryRisk(items)

  // No compliance matrix yet — AiReadinessBanner above already owns that story.
  if (relevant.length === 0) return null

  if (!hasAnyPosition) {
    return (
      <div className="rounded-lg bg-muted/40 px-4 py-3">
        <span className="text-xs font-medium text-muted-foreground">Compliance position</span>
        <p className="mt-1.5 text-meta">
          {relevant.length} requirement{relevant.length === 1 ? '' : 's'} extracted, none
          assessed — nothing here tells you whether you can meet them.{' '}
          <Link
            to={complianceMatrixLink(bidId)}
            className="text-[hsl(var(--link))] underline-offset-2 hover:underline"
          >
            Assess in the compliance matrix
          </Link>
        </p>
      </div>
    )
  }

  if (atRisk === 0) return null

  return (
    <div className="rounded-lg bg-muted/40 px-4 py-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--destructive))]" />
        <span className="text-xs font-medium text-muted-foreground">Compliance position</span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        {nonComply > 0 && (
          <Link
            to={complianceMatrixLink(bidId, { position: 'non_comply', criticality: 'mandatory' })}
            className="text-[hsl(var(--destructive))] underline-offset-2 hover:underline"
          >
            {nonComply} mandatory non-comply
          </Link>
        )}
        {nonComply > 0 && needsReview > 0 && <span aria-hidden="true">·</span>}
        {needsReview > 0 && (
          <Link
            // Every unresolved label the count includes: "Needs review", "Needs your confirmation", "Not assessed".
            to={complianceMatrixLink(bidId, {
              position: MANDATORY_UNRESOLVED_STATES.join(','),
              criticality: 'mandatory',
            })}
            className="text-[hsl(var(--status-warning))] underline-offset-2 hover:underline"
          >
            {needsReview} mandatory {reviewCountPhrase(needsReview, needsConfirmation, 'needs review')}
          </Link>
        )}
      </div>
      <p className="mt-2 text-meta">
        Resolve or accept these before committing the team — they are pass/fail requirements.
      </p>
    </div>
  )
}
